import { HoursField } from "@superadmin/components/HoursField";
import { PageShell } from "@superadmin/components/PageShell";
import { formatMs } from "@superadmin/utils/format";
import { orpc } from "@superadmin/utils/orpc.tanstack.client";
import { useQuery } from "@tanstack/react-query";
import { LoadingSpinner } from "@voicely/ui-mui/components/LoadingSpinner";
import { Chip } from "@voicely/ui-mui/data-display/Chip";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@voicely/ui-mui/data-display/Table";
import { Typography } from "@voicely/ui-mui/data-display/Typography";
import { Alert } from "@voicely/ui-mui/feedback/Alert";
import { Box } from "@voicely/ui-mui/layout/Box";
import { Card } from "@voicely/ui-mui/layout/Card";
import { Grid } from "@voicely/ui-mui/layout/Grid";
import { Paper } from "@voicely/ui-mui/layout/Paper";
import { Stack } from "@voicely/ui-mui/layout/Stack";
import { useUrlState } from "@voicely/web-shared/use_url_state";
import { z } from "zod";

const hoursSchema = z.coerce.number().int().min(1).max(168);

const Stat = ({
	label,
	value,
	alarming = false,
}: {
	label: string;
	value: string | number;
	/** Painted red only when non-zero would mean callers heard silence. */
	alarming?: boolean;
}) => (
	<Card variant="outlined" sx={{ p: 2 }}>
		<Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
			{label}
		</Typography>
		<Typography
			variant="h4"
			sx={{ color: alarming && Number(value) > 0 ? "error.main" : "text.primary" }}
		>
			{value}
		</Typography>
	</Card>
);

/** Fleet-wide rollups: doc 17 § 5–8's numbers without SSH. */
export const DashboardPage = () => {
	const [hours, setHours] = useUrlState("hours", hoursSchema, 24);

	const statsQuery = useQuery(
		orpc.superAdmin.fleetStats.queryOptions({ input: { withinHours: hours } }),
	);

	const toolbar = <HoursField value={hours} onChange={setHours} />;

	if (statsQuery.isPending) {
		return (
			<PageShell title="Fleet" toolbar={toolbar}>
				<LoadingSpinner />
			</PageShell>
		);
	}

	if (statsQuery.error) {
		return (
			<PageShell title="Fleet" toolbar={toolbar}>
				<Alert severity="error">{statsQuery.error.message}</Alert>
			</PageShell>
		);
	}

	const stats = statsQuery.data;

	const distributions = [
		{ label: "Carrier backlog (at the caller)", value: stats.carrierBacklogMs },
		{ label: "Utterance hold (endpointing wait)", value: stats.utteranceHoldMs },
		{ label: "Response latency", value: stats.responseLatencyMs },
	];

	return (
		<PageShell
			title="Fleet"
			subtitle={`Every workspace, last ${stats.windowHours}h`}
			toolbar={toolbar}
		>
			<Stack spacing={3}>
				<Grid container spacing={2}>
					<Grid size={{ xs: 6, sm: 3 }}>
						<Stat label="Sessions" value={stats.sessions} />
					</Grid>
					<Grid size={{ xs: 6, sm: 3 }}>
						<Stat label="Turns" value={stats.turns} />
					</Grid>
					<Grid size={{ xs: 6, sm: 3 }}>
						<Stat label="Agent turns" value={stats.agentTurns} />
					</Grid>
					<Grid size={{ xs: 6, sm: 3 }}>
						<Stat label="Silent agent turns" value={stats.silentAgentTurns} alarming />
					</Grid>
				</Grid>

				{Object.keys(stats.sessionsByStatus).length > 0 && (
					<Box>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							Sessions by status
						</Typography>
						<Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
							{Object.entries(stats.sessionsByStatus).map(([status, count]) => (
								<Chip key={status} label={`${status}: ${count}`} size="small" />
							))}
						</Stack>
					</Box>
				)}

				<Card variant="outlined" sx={{ p: 2 }}>
					<Typography variant="subtitle2" sx={{ mb: 1.5 }}>
						Barge-in
					</Typography>
					<Grid container spacing={2}>
						<Grid size={{ xs: 6, sm: 4 }}>
							<Stat label="Cancellations" value={stats.barge.cancellations} />
						</Grid>
						<Grid size={{ xs: 6, sm: 4 }}>
							<Stat
								label="Cancelled before any audio"
								value={stats.barge.cancelledBeforeAnyAudio}
								alarming
							/>
						</Grid>
						<Grid size={{ xs: 6, sm: 4 }}>
							<Stat label="Weak-score cancellations" value={stats.barge.weakScoreCancellations} />
						</Grid>
						<Grid size={{ xs: 6, sm: 4 }}>
							<Stat
								label="Interrupted w/o evidence"
								value={stats.barge.interruptedWithoutEvidence}
							/>
						</Grid>
						<Grid size={{ xs: 6, sm: 4 }}>
							<Stat label="Mean Silero score" value={stats.barge.meanScore?.toFixed(2) ?? "—"} />
						</Grid>
						<Grid size={{ xs: 6, sm: 4 }}>
							<Stat label="Mean speech" value={formatMs(stats.barge.meanSpeechMs)} />
						</Grid>
						<Grid size={{ xs: 6, sm: 4 }}>
							<Stat
								label="Cut rate (per agent turn)"
								value={
									stats.barge.cancellationRate === null
										? "—"
										: `${Math.round(stats.barge.cancellationRate * 100)}%`
								}
							/>
						</Grid>
					</Grid>
				</Card>

				{/* Beside barge-in rather than inside it: these are the replies that
				    were NOT cut, which is the classifier's whole case and the one
				    number above cannot show (doc 12 § 9.0b). */}
				<Card variant="outlined" sx={{ p: 2 }}>
					<Typography variant="subtitle2" sx={{ mb: 1.5 }}>
						Barge-in classifier
					</Typography>
					<Grid container spacing={2}>
						<Grid size={{ xs: 6, sm: 4 }}>
							<Stat label="“Haan” let through" value={stats.classifier.dismissed} />
						</Grid>
						<Grid size={{ xs: 6, sm: 4 }}>
							<Stat
								label="Replies it kept playing"
								value={stats.classifier.repliesWithDismissals}
							/>
						</Grid>
						<Grid size={{ xs: 6, sm: 4 }}>
							<Stat
								label="Talked over while deciding"
								value={formatMs(stats.classifier.meanClassifyMs)}
							/>
						</Grid>
						<Grid size={{ xs: 6, sm: 4 }}>
							<Stat label="Cut on words" value={stats.classifier.byVerdict.words} />
						</Grid>
						<Grid size={{ xs: 6, sm: 4 }}>
							<Stat label="Cut, still talking" value={stats.classifier.byVerdict.sustained} />
						</Grid>
						<Grid size={{ xs: 6, sm: 4 }}>
							<Stat label="Cut in first second" value={stats.classifier.byVerdict.early} />
						</Grid>
						<Grid size={{ xs: 6, sm: 4 }}>
							<Stat label="Cut, not classified" value={stats.classifier.byVerdict.unclassified} />
						</Grid>
					</Grid>
				</Card>

				<TableContainer component={Paper} variant="outlined">
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Distribution</TableCell>
								<TableCell align="right">Count</TableCell>
								<TableCell align="right">p50</TableCell>
								<TableCell align="right">p90</TableCell>
								<TableCell align="right">p99</TableCell>
								<TableCell align="right">Max</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{distributions.map((row) => (
								<TableRow key={row.label}>
									<TableCell>{row.label}</TableCell>
									<TableCell align="right">{row.value.count}</TableCell>
									<TableCell align="right">{formatMs(row.value.p50)}</TableCell>
									<TableCell align="right">{formatMs(row.value.p90)}</TableCell>
									<TableCell align="right">{formatMs(row.value.p99)}</TableCell>
									<TableCell align="right">{formatMs(row.value.max)}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</Stack>
		</PageShell>
	);
};
